import { useState, useEffect, useRef, useCallback } from 'react';
import PageHero from '../../components/feature/PageHero';

// ─── TYPES ─────────────────────────────────────────────────────────────────────
interface SpreadGame {
  id: string;
  round: string;
  region: string;
  tipoff: string;
  isLive: boolean;
  livePeriod?: string;
  liveTime?: string;
  homeTeam: string;
  homeSeed: number;
  awayTeam: string;
  awaySeed: number;
  homeSpread: number;
  awaySpread: number;
  homeSpreadOdds: number;
  awaySpreadOdds: number;
  homeML: number;
  awayML: number;
  total: number;
  overOdds: number;
  underOdds: number;
  volume24h: string;
}

interface FutureTeam {
  team: string;
  seed: number;
  region: string;
  prob: number;
  americanOdds: number;
}

interface FuturesMarket {
  id: string;
  title: string;
  badge: string;
  badgeColor: string;
  volume24h: string;
  teams: FutureTeam[];
}

interface PlayerProp {
  id: string;
  player: string;
  team: string;
  game: string;
  round: string;
  prop: string;
  line: number;
  overOdds: number;
  underOdds: number;
  volume24h: string;
}

// ─── SPREAD GAMES DATA ─────────────────────────────────────────────────────────
const BASE_SPREADS: SpreadGame[] = [
  // WEST REGION — Saturday Mar 21
  {
    id: 'w1', round: 'Round of 32', region: 'West', tipoff: 'Mar 21 • 12:10 PM ET',
    isLive: true, livePeriod: '2nd Half', liveTime: '6:22',
    homeTeam: 'Michigan', homeSeed: 1, awayTeam: 'Saint Louis', awaySeed: 9,
    homeSpread: -12.5, awaySpread: 12.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -700, awayML: +480, total: 161.5, overOdds: -110, underOdds: -110,
    volume24h: '$384K',
  },
  {
    id: 'w2', round: 'Round of 32', region: 'West', tipoff: 'Mar 21 • 2:40 PM ET',
    isLive: true, livePeriod: '1st Half', liveTime: '11:05',
    homeTeam: 'Gonzaga', homeSeed: 3, awayTeam: 'Texas', awaySeed: 11,
    homeSpread: -6.5, awaySpread: 6.5, homeSpreadOdds: -112, awaySpreadOdds: -108,
    homeML: -275, awayML: +225, total: 146.5, overOdds: -110, underOdds: -110,
    volume24h: '$218K',
  },
  // EAST REGION — Saturday Mar 21
  {
    id: 'e1', round: 'Round of 32', region: 'East', tipoff: 'Mar 21 • 5:15 PM ET',
    isLive: false,
    homeTeam: 'Michigan State', homeSeed: 3, awayTeam: 'Louisville', awaySeed: 6,
    homeSpread: -4.5, awaySpread: 4.5, homeSpreadOdds: -112, awaySpreadOdds: -108,
    homeML: -185, awayML: +155, total: 150.5, overOdds: -110, underOdds: -110,
    volume24h: '$197K',
  },
  {
    id: 'e2', round: 'Round of 32', region: 'East', tipoff: 'Mar 21 • 7:45 PM ET',
    isLive: false,
    homeTeam: 'Duke', homeSeed: 1, awayTeam: 'TCU', awaySeed: 9,
    homeSpread: -11.5, awaySpread: 11.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -600, awayML: +420, total: 140.5, overOdds: -110, underOdds: -110,
    volume24h: '$341K',
  },
  // SOUTH REGION — Saturday Mar 21
  {
    id: 's1', round: 'Round of 32', region: 'South', tipoff: 'Mar 21 • 6:30 PM ET',
    isLive: false,
    homeTeam: 'Houston', homeSeed: 2, awayTeam: 'Texas A&M', awaySeed: 10,
    homeSpread: -10.5, awaySpread: 10.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -550, awayML: +390, total: 142.5, overOdds: -112, underOdds: -108,
    volume24h: '$256K',
  },
  {
    id: 's2', round: 'Round of 32', region: 'South', tipoff: 'Mar 21 • 9:00 PM ET',
    isLive: false,
    homeTeam: 'Arkansas', homeSeed: 4, awayTeam: 'High Point', awaySeed: 12,
    homeSpread: -11.5, awaySpread: 11.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -620, awayML: +450, total: 168.5, overOdds: -108, underOdds: -112,
    volume24h: '$164K',
  },
  // MIDWEST REGION — Saturday Mar 21
  {
    id: 'm1', round: 'Round of 32', region: 'Midwest', tipoff: 'Mar 21 • 4:30 PM ET',
    isLive: false,
    homeTeam: 'Vanderbilt', homeSeed: 5, awayTeam: 'Nebraska', awaySeed: 4,
    homeSpread: -1.5, awaySpread: 1.5, homeSpreadOdds: -112, awaySpreadOdds: -108,
    homeML: -120, awayML: +100, total: 147.5, overOdds: -110, underOdds: -110,
    volume24h: '$143K',
  },
  {
    id: 'm2', round: 'Round of 32', region: 'Midwest', tipoff: 'Mar 21 • 8:45 PM ET',
    isLive: false,
    homeTeam: 'Illinois', homeSeed: 3, awayTeam: 'VCU', awaySeed: 11,
    homeSpread: -10.5, awaySpread: 10.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -550, awayML: +390, total: 151.5, overOdds: -110, underOdds: -110,
    volume24h: '$188K',
  },
  // WEST REGION — Sunday Mar 22
  {
    id: 'w3', round: 'Round of 32', region: 'West', tipoff: 'Mar 22 • 12:15 PM ET',
    isLive: false,
    homeTeam: 'Arizona', homeSeed: 1, awayTeam: 'Utah State', awaySeed: 9,
    homeSpread: -11.5, awaySpread: 11.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -600, awayML: +420, total: 145.5, overOdds: -110, underOdds: -110,
    volume24h: '$302K',
  },
  {
    id: 'w4', round: 'Round of 32', region: 'West', tipoff: 'Mar 22 • 5:15 PM ET',
    isLive: false,
    homeTeam: 'Tennessee', homeSeed: 6, awayTeam: 'Virginia', awaySeed: 3,
    homeSpread: -1.5, awaySpread: 1.5, homeSpreadOdds: -115, awaySpreadOdds: -105,
    homeML: -125, awayML: +105, total: 133.5, overOdds: -110, underOdds: -110,
    volume24h: '$211K',
  },
  // EAST REGION — Sunday Mar 22
  {
    id: 'e3', round: 'Round of 32', region: 'East', tipoff: 'Mar 22 • 2:45 PM ET',
    isLive: false,
    homeTeam: 'Purdue', homeSeed: 2, awayTeam: 'Miami FL', awaySeed: 7,
    homeSpread: -7.5, awaySpread: 7.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -320, awayML: +260, total: 148.0, overOdds: -110, underOdds: -110,
    volume24h: '$178K',
  },
  {
    id: 'e4', round: 'Round of 32', region: 'East', tipoff: 'Mar 22 • 7:30 PM ET',
    isLive: false,
    homeTeam: 'UConn', homeSeed: 2, awayTeam: 'UCLA', awaySeed: 7,
    homeSpread: -4.5, awaySpread: 4.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -185, awayML: +155, total: 140.5, overOdds: -112, underOdds: -108,
    volume24h: '$289K',
  },
  // SOUTH REGION — Sunday Mar 22
  {
    id: 's3', round: 'Round of 32', region: 'South', tipoff: 'Mar 22 • 4:30 PM ET',
    isLive: false,
    homeTeam: 'Florida', homeSeed: 1, awayTeam: 'Iowa', awaySeed: 9,
    homeSpread: -10.5, awaySpread: 10.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -550, awayML: +390, total: 154.5, overOdds: -110, underOdds: -110,
    volume24h: '$267K',
  },
  {
    id: 's4', round: 'Round of 32', region: 'South', tipoff: 'Mar 22 • 9:30 PM ET',
    isLive: false,
    homeTeam: 'Alabama', homeSeed: 4, awayTeam: 'Texas Tech', awaySeed: 5,
    homeSpread: -1.5, awaySpread: 1.5, homeSpreadOdds: -112, awaySpreadOdds: -108,
    homeML: -120, awayML: +100, total: 149.5, overOdds: -110, underOdds: -110,
    volume24h: '$152K',
  },
  // MIDWEST REGION — Sunday Mar 22
  {
    id: 'mk1', round: 'Round of 32', region: 'Midwest', tipoff: 'Mar 22 • 3:00 PM ET',
    isLive: false,
    homeTeam: 'Iowa State', homeSeed: 2, awayTeam: 'Kentucky', awaySeed: 7,
    homeSpread: -4.5, awaySpread: 4.5, homeSpreadOdds: -110, awaySpreadOdds: -110,
    homeML: -185, awayML: +155, total: 151.0, overOdds: -110, underOdds: -110,
    volume24h: '$224K',
  },
  {
    id: 'mk2', round: 'Round of 32', region: 'Midwest', tipoff: 'Mar 22 • 8:00 PM ET',
    isLive: false,
    homeTeam: "St. John's", homeSeed: 5, awayTeam: 'Kansas', awaySeed: 4,
    homeSpread: -3.5, awaySpread: 3.5, homeSpreadOdds: -112, awaySpreadOdds: -108,
    homeML: -165, awayML: +140, total: 142.0, overOdds: -110, underOdds: -110,
    volume24h: '$193K',
  },
];

// ─── FUTURES DATA ──────────────────────────────────────────────────────────────
const BASE_FUTURES: FuturesMarket[] = [
  {
    id: 'champ', title: 'NCAA Championship Winner', badge: 'Championship', badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    volume24h: '$2.4M',
    teams: [
      { team: 'Arizona', seed: 1, region: 'West', prob: 22, americanOdds: +340 },
      { team: 'Michigan', seed: 1, region: 'West', prob: 21, americanOdds: +370 },
      { team: 'Duke', seed: 1, region: 'East', prob: 18, americanOdds: +450 },
      { team: 'Florida', seed: 1, region: 'South', prob: 11, americanOdds: +800 },
      { team: 'Houston', seed: 2, region: 'South', prob: 10, americanOdds: +900 },
      { team: 'Iowa State', seed: 2, region: 'Midwest', prob: 7, americanOdds: +1300 },
      { team: 'UConn', seed: 2, region: 'East', prob: 5, americanOdds: +1900 },
      { team: 'Gonzaga', seed: 3, region: 'West', prob: 3, americanOdds: +3100 },
      { team: "St. John's", seed: 5, region: 'Midwest', prob: 2, americanOdds: +4800 },
      { team: 'Field', seed: 0, region: 'Various', prob: 1, americanOdds: +8500 },
    ],
  },
  {
    id: 'final4e', title: 'Final Four — East Region', badge: 'Final Four', badgeColor: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    volume24h: '$641K',
    teams: [
      { team: 'Duke', seed: 1, region: 'East', prob: 48, americanOdds: +108 },
      { team: 'Purdue', seed: 2, region: 'East', prob: 24, americanOdds: +317 },
      { team: 'UConn', seed: 2, region: 'East', prob: 19, americanOdds: +426 },
      { team: 'Michigan State', seed: 3, region: 'East', prob: 9, americanOdds: +1011 },
    ],
  },
  {
    id: 'final4w', title: 'Final Four — West Region', badge: 'Final Four', badgeColor: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    volume24h: '$587K',
    teams: [
      { team: 'Arizona', seed: 1, region: 'West', prob: 44, americanOdds: +127 },
      { team: 'Michigan', seed: 1, region: 'West', prob: 33, americanOdds: +203 },
      { team: 'Gonzaga', seed: 3, region: 'West', prob: 15, americanOdds: +567 },
      { team: 'Tennessee', seed: 6, region: 'West', prob: 8, americanOdds: +1150 },
    ],
  },
  {
    id: 'final4s', title: 'Final Four — South Region', badge: 'Final Four', badgeColor: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    volume24h: '$512K',
    teams: [
      { team: 'Florida', seed: 1, region: 'South', prob: 39, americanOdds: +156 },
      { team: 'Houston', seed: 2, region: 'South', prob: 31, americanOdds: +223 },
      { team: 'Arkansas', seed: 4, region: 'South', prob: 17, americanOdds: +488 },
      { team: 'Alabama', seed: 4, region: 'South', prob: 13, americanOdds: +669 },
    ],
  },
  {
    id: 'final4m', title: 'Final Four — Midwest Region', badge: 'Final Four', badgeColor: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    volume24h: '$478K',
    teams: [
      { team: 'Iowa State', seed: 2, region: 'Midwest', prob: 37, americanOdds: +170 },
      { team: 'Illinois', seed: 3, region: 'Midwest', prob: 28, americanOdds: +257 },
      { team: "St. John's", seed: 5, region: 'Midwest', prob: 21, americanOdds: +376 },
      { team: 'Vanderbilt', seed: 5, region: 'Midwest', prob: 14, americanOdds: +614 },
    ],
  },
];

// ─── PLAYER PROPS DATA ─────────────────────────────────────────────────────────
const PLAYER_PROPS: PlayerProp[] = [
  { id: 'pp1', player: 'Cooper Flagg', team: 'Duke', game: 'Duke vs TCU', round: 'Round of 32', prop: 'Points', line: 22.5, overOdds: -118, underOdds: -102, volume24h: '$128K' },
  { id: 'pp2', player: 'Cooper Flagg', team: 'Duke', game: 'Duke vs TCU', round: 'Round of 32', prop: 'Rebounds', line: 8.5, overOdds: -112, underOdds: -108, volume24h: '$74K' },
  { id: 'pp3', player: 'LJ Cryer', team: 'Houston', game: 'Houston vs Texas A&M', round: 'Round of 32', prop: 'Points', line: 16.5, overOdds: -110, underOdds: -110, volume24h: '$61K' },
  { id: 'pp4', player: 'Caleb Love', team: 'Arizona', game: 'Arizona vs Utah State', round: 'Round of 32', prop: 'Points', line: 18.5, overOdds: -112, underOdds: -108, volume24h: '$88K' },
  { id: 'pp5', player: 'Caleb Love', team: 'Arizona', game: 'Arizona vs Utah State', round: 'Round of 32', prop: '3-Pointers Made', line: 2.5, overOdds: +105, underOdds: -125, volume24h: '$52K' },
  { id: 'pp6', player: 'Ryan Nembhard', team: 'Gonzaga', game: 'Gonzaga vs Texas', round: 'Round of 32', prop: 'Assists', line: 5.5, overOdds: -115, underOdds: -105, volume24h: '$44K' },
  { id: 'pp7', player: 'Walter Clayton Jr.', team: 'Florida', game: 'Florida vs Iowa', round: 'Round of 32', prop: 'Points', line: 17.5, overOdds: -110, underOdds: -110, volume24h: '$67K' },
  { id: 'pp8', player: 'Tyson Walker', team: 'Michigan State', game: 'Michigan State vs Louisville', round: 'Round of 32', prop: 'Points + Assists', line: 22.5, overOdds: -112, underOdds: -108, volume24h: '$39K' },
  { id: 'pp9', player: 'Tamin Lipsey', team: 'Iowa State', game: 'Iowa State vs Kentucky', round: 'Round of 32', prop: 'Assists', line: 4.5, overOdds: -108, underOdds: -112, volume24h: '$35K' },
  { id: 'pp10', player: 'Roddy Gayle Jr.', team: 'Michigan', game: 'Michigan vs Saint Louis', round: 'Round of 32', prop: 'Points', line: 14.5, overOdds: +100, underOdds: -120, volume24h: '$29K' },
];

// ─── FORMAT HELPERS ────────────────────────────────────────────────────────────
function formatML(ml: number): string {
  return ml > 0 ? `+${ml}` : `${ml}`;
}

function mlToImpliedProb(ml: number): string {
  if (ml < 0) return `${Math.round((-ml / (-ml + 100)) * 100)}%`;
  return `${Math.round((100 / (ml + 100)) * 100)}%`;
}

// ─── SPREAD CARD ───────────────────────────────────────────────────────────────
function SpreadCard({ game, onNudge }: { game: SpreadGame; onNudge: (id: string, field: string, delta: number) => void }) {
  const [betAdded, setBetAdded] = useState<string | null>(null);

  const handleAdd = (type: string) => {
    setBetAdded(type);
    setTimeout(() => setBetAdded(null), 1600);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 overflow-hidden hover:border-slate-500/80 transition-all">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {game.isLive ? (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-400 text-xs font-bold animate-pulse">
                <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>LIVE
              </span>
            ) : (
              <span className="text-xs text-slate-500 bg-slate-800/60 px-2 py-0.5 rounded-full border border-slate-700/50 flex items-center gap-1">
                <i className="ri-time-line"></i>{game.tipoff}
              </span>
            )}
            {game.isLive && (
              <span className="text-xs text-slate-400">{game.livePeriod} • {game.liveTime}</span>
            )}
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30 font-semibold">
            {game.region} • {game.round}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white font-bold text-base">
              <span className="text-slate-500 text-xs mr-1">#{game.awaySeed}</span>{game.awayTeam}
            </div>
            <div className="text-white font-bold text-base mt-1">
              <span className="text-slate-500 text-xs mr-1">#{game.homeSeed}</span>{game.homeTeam}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div className="text-teal-500 font-semibold text-xs">Vol: {game.volume24h}</div>
          </div>
        </div>
      </div>

      {/* Betting Rows */}
      <div className="px-3 py-3 space-y-2 text-sm">
        {/* Spread Row */}
        <div className="grid grid-cols-3 gap-1 text-center text-xs text-slate-500 mb-1">
          <span></span><span>Spread</span><span>Money Line</span>
        </div>
        {/* Away */}
        <div className="grid grid-cols-3 gap-1 items-center">
          <span className="text-slate-300 text-xs font-semibold truncate">{game.awayTeam}</span>
          <button
            onClick={() => handleAdd(`away-spread`)}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'away-spread' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            {game.awaySpread > 0 ? `+${game.awaySpread}` : game.awaySpread} <span className="text-slate-500">({formatML(game.awaySpreadOdds)})</span>
          </button>
          <button
            onClick={() => handleAdd(`away-ml`)}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'away-ml' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            {formatML(game.awayML)}
          </button>
        </div>
        {/* Home */}
        <div className="grid grid-cols-3 gap-1 items-center">
          <span className="text-slate-300 text-xs font-semibold truncate">{game.homeTeam}</span>
          <button
            onClick={() => handleAdd(`home-spread`)}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'home-spread' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            {game.homeSpread > 0 ? `+${game.homeSpread}` : game.homeSpread} <span className="text-slate-500">({formatML(game.homeSpreadOdds)})</span>
          </button>
          <button
            onClick={() => handleAdd(`home-ml`)}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'home-ml' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            {formatML(game.homeML)}
          </button>
        </div>
        {/* Total */}
        <div className="mt-2 pt-2 border-t border-slate-700/40 grid grid-cols-2 gap-1">
          <button
            onClick={() => handleAdd('over')}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'over' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            O {game.total} <span className="text-slate-500">({formatML(game.overOdds)})</span>
          </button>
          <button
            onClick={() => handleAdd('under')}
            className={`py-1.5 rounded text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${betAdded === 'under' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
          >
            U {game.total} <span className="text-slate-500">({formatML(game.underOdds)})</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FUTURES CARD ──────────────────────────────────────────────────────────────
function FuturesCard({ market }: { market: FuturesMarket }) {
  const [selected, setSelected] = useState(market.teams[0].team);
  const [added, setAdded] = useState(false);
  const sel = market.teams.find(t => t.team === selected) || market.teams[0];

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 overflow-hidden hover:border-slate-500/80 transition-all flex flex-col">
      <div className="px-5 pt-5 pb-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${market.badgeColor}`}>{market.badge}</span>
          <span className="text-xs text-teal-500 font-semibold">Vol: {market.volume24h}</span>
        </div>
        <h3 className="text-base font-bold text-white">{market.title}</h3>
      </div>
      <div className="flex-1 p-4 space-y-1.5 overflow-y-auto" style={{ maxHeight: 260 }}>
        {market.teams.map((t, i) => (
          <div
            key={i}
            onClick={() => setSelected(t.team)}
            className={`cursor-pointer p-2 rounded-lg border transition-all flex items-center justify-between ${t.team === selected ? 'bg-teal-500/10 border-teal-500/50' : 'bg-white/5 border-slate-700/70 hover:border-slate-500'}`}
          >
            <div className="flex items-center gap-2">
              {t.seed > 0 && <span className="text-xs text-slate-500 w-5">#{t.seed}</span>}
              <span className={`text-sm font-semibold ${t.team === selected ? 'text-teal-400' : 'text-slate-200'}`}>{t.team}</span>
              {t.region !== 'Various' && <span className="text-xs text-slate-600 hidden sm:block">{t.region}</span>}
            </div>
            <div className="text-right">
              <div className={`text-sm font-bold ${t.americanOdds > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>{formatML(t.americanOdds)}</div>
              <div className="text-xs text-slate-500">{t.prob}%</div>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 border-t border-slate-700/50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400">Selected: <span className="text-white font-semibold">{sel.team}</span></div>
            <div className={`text-sm font-bold ${sel.americanOdds > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>{formatML(sel.americanOdds)} · {sel.prob}%</div>
          </div>
          <button
            onClick={() => { setAdded(true); setTimeout(() => setAdded(false), 1600); }}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${added ? 'bg-emerald-500 text-white' : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white'}`}
          >
            {added ? <><i className="ri-check-line mr-1"></i>Added</> : 'Add to Slip'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PLAYER PROP CARD ──────────────────────────────────────────────────────────
function PlayerPropCard({ prop }: { prop: PlayerProp }) {
  const [bet, setBet] = useState<'over' | 'under' | null>(null);
  const [added, setAdded] = useState(false);

  const handleBet = (side: 'over' | 'under') => {
    setBet(side);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 p-4 hover:border-slate-500/80 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-white font-bold text-base">{prop.player}</div>
          <div className="text-xs text-slate-400">{prop.team} · {prop.round}</div>
          <div className="text-xs text-slate-500 mt-0.5">{prop.game}</div>
        </div>
        <span className="text-xs text-teal-500 font-semibold">Vol: {prop.volume24h}</span>
      </div>
      <div className="text-center mb-3">
        <div className="text-xs text-slate-500 mb-0.5">{prop.prop}</div>
        <div className="text-2xl font-extrabold text-white">{prop.line}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => handleBet('over')}
          className={`py-2 rounded-lg text-sm font-bold border transition-all cursor-pointer whitespace-nowrap ${bet === 'over' && added ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
        >
          Over {formatML(prop.overOdds)}
        </button>
        <button
          onClick={() => handleBet('under')}
          className={`py-2 rounded-lg text-sm font-bold border transition-all cursor-pointer whitespace-nowrap ${bet === 'under' && added ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-slate-700/70 text-slate-200 hover:border-teal-500/50 hover:text-teal-400'}`}
        >
          Under {formatML(prop.underOdds)}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
const TABS = ['All', 'Spreads', 'Futures', 'Player Props'] as const;
type Tab = typeof TABS[number];

const REGIONS = ['All Regions', 'East', 'West', 'South', 'Midwest'];

// ─── SORT HELPER ───────────────────────────────────────────────────────────────
function getGameDay(tipoff: string): number {
  if (tipoff.startsWith('Mar 21')) return 21;
  if (tipoff.startsWith('Mar 22')) return 22;
  return 99;
}

export default function MarchMadnessPage() {
  const [activeTab, setActiveTab] = useState<Tab>('All');
  const [activeRegion, setActiveRegion] = useState('All Regions');
  const [sortOrder, setSortOrder] = useState<'sat-first' | 'sun-first'>('sat-first');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [secsAgo, setSecsAgo] = useState(0);
  const [games, setGames] = useState<SpreadGame[]>(BASE_SPREADS);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  // Simulated live odds refresh every 28 seconds
  const nudgeOdds = useCallback(() => {
    setGames(prev => prev.map(g => ({
      ...g,
      homeSpreadOdds: Math.max(-130, Math.min(-90, g.homeSpreadOdds + (Math.random() > 0.5 ? 2 : -2))),
      awaySpreadOdds: Math.max(-130, Math.min(-90, g.awaySpreadOdds + (Math.random() > 0.5 ? 2 : -2))),
      overOdds: Math.max(-130, Math.min(-90, g.overOdds + (Math.random() > 0.5 ? 1 : -1))),
      underOdds: Math.max(-130, Math.min(-90, g.underOdds + (Math.random() > 0.5 ? 1 : -1))),
    })));
    setLastUpdated(new Date());
    setSecsAgo(0);
  }, []);

  useEffect(() => {
    const refreshInterval = setInterval(nudgeOdds, 28000);
    const clockInterval = setInterval(() => setSecsAgo(s => s + 1), 1000);
    return () => { clearInterval(refreshInterval); clearInterval(clockInterval); };
  }, [nudgeOdds]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-march-madness';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'March Madness Prediction Markets \u2014 L2L Sports Exchange',
        description: 'Trade NCAA March Madness Round of 32 spreads, totals, futures and player props. Zero juice peer-to-peer pricing on every matchup.',
        url: `${siteUrl}/march-madness`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: 'NCAA March Madness Tournament 2026',
        sport: 'Basketball',
        startDate: '2026-03-19',
        endDate: '2026-04-06',
        location: {
          '@type': 'Place',
          name: 'Various NCAA Venues, USA',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is March Madness?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'March Madness is the NCAA Division I Men\'s Basketball Tournament, a 68-team single-elimination bracket tournament held each spring. The Round of 32 features 16 games across four regions: East, West, South, and Midwest.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do spread bets work on L2L?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'On L2L you buy shares on whether a team covers the spread. If the favorite is -7.5, you bet they win by 8 or more. Each winning share pays $1.00. You can sell your shares at any time before settlement.',
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const filteredGames = (() => {
    const regionFiltered = activeRegion === 'All Regions' ? games : games.filter(g => g.region === activeRegion);
    return [...regionFiltered].sort((a, b) => {
      const diff = getGameDay(a.tipoff) - getGameDay(b.tipoff);
      return sortOrder === 'sat-first' ? diff : -diff;
    });
  })();

  const liveCount = games.filter(g => g.isLive).length;

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-2">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="L2L Logo" className="h-full w-full object-contain" />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness', active: true },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${(item as any).active ? 'bg-orange-500/20 text-orange-400' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'}`}>
                    <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {item.label}
                  </div>
                </a>
              ))}
              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef}>
                <button onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    {[
                      { label: 'Spreads', icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400', path: '/markets/spreads' },
                      { label: 'Money Lines', icon: 'ri-copper-coin-line', color: 'text-amber-400', path: '/markets/money-lines' },
                      { label: 'Futures', icon: 'ri-calendar-event-line', color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays', icon: 'ri-stack-line', color: 'text-violet-400', path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line', color: 'text-cyan-400', path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:text-slate-200 hover:bg-slate-800/60 cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span><i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              {[
                { href: '/why-l2l', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                    <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {item.label}
                  </div>
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <a href="/my-account">
                <div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg cursor-pointer" aria-label="Toggle menu">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
                { href: '/why-l2l', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:text-slate-300 hover:bg-slate-800/60 cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span>{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        badgeLabel="Round of 32 • Live Now"
        badgeColor="yellow"
        title="March Madness Markets"
        subtitle="Trade spreads, totals &amp; futures on every Round of 32 matchup. Zero juice peer-to-peer pricing — odds update live."
        backgroundImage="https://public.readdy.ai/ai/img_res/301832aa16e268a6a342108795d3aab2.jpg"
      />

      {/* Live Stats Bar */}
      <div className="border-y border-slate-700/50" style={{ background: 'rgba(30,30,30,0.8)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-center">
            {[
              { label: 'Live Games', value: String(liveCount), icon: 'ri-live-line', color: 'text-rose-400' },
              { label: 'Round of 32', value: '16', icon: 'ri-basketball-line', color: 'text-orange-400' },
              { label: '24h Volume', value: '$4.6M', icon: 'ri-line-chart-line', color: 'text-teal-400' },
              { label: 'Futures', value: String(BASE_FUTURES.length), icon: 'ri-calendar-event-line', color: 'text-emerald-400' },
              { label: 'Player Props', value: String(PLAYER_PROPS.length), icon: 'ri-user-star-line', color: 'text-amber-400' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <i className={`${s.icon} text-lg sm:text-xl ${s.color}`}></i>
                <div className={`text-lg sm:text-xl font-extrabold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-slate-500 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs + Live ticker */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-1 min-w-0 pb-0.5">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${activeTab === tab ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500 flex-shrink-0">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
            Odds updated {secsAgo < 5 ? 'just now' : `${secsAgo}s ago`}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">

          {/* SPREADS SECTION */}
          {(activeTab === 'All' || activeTab === 'Spreads') && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2 flex-wrap">
                    <i className="ri-bar-chart-grouped-line text-teal-400"></i>
                    Spreads &amp; Totals — Round of 32
                    {liveCount > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-500/20 border border-rose-500/40 rounded-full text-rose-400 text-xs font-bold animate-pulse">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>{liveCount} LIVE
                      </span>
                    )}
                  </h2>
                  <p className="text-slate-500 text-sm mt-0.5">Click any line to add to your bet slip</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {/* Sort Toggle */}
                  <button
                    onClick={() => setSortOrder(s => s === 'sat-first' ? 'sun-first' : 'sat-first')}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap bg-slate-800/80 border-slate-600/60 text-slate-300 hover:border-orange-500/50 hover:text-orange-400 flex-shrink-0"
                  >
                    <i className="ri-sort-asc text-orange-400"></i>
                    <span>
                      {sortOrder === 'sat-first' ? 'Sat → Sun' : 'Sun → Sat'}
                    </span>
                    <span className="text-slate-600">|</span>
                    <span className="text-slate-500 text-xs">Game Time</span>
                  </button>
                  {/* Region Filters */}
                  <div className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-0.5">
                    {REGIONS.map(r => (
                      <button key={r} onClick={() => setActiveRegion(r)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${activeRegion === r ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:text-slate-300'}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredGames.map(g => (
                  <SpreadCard key={g.id} game={g} onNudge={() => {}} />
                ))}
              </div>
            </div>
          )}

          {/* FUTURES SECTION */}
          {(activeTab === 'All' || activeTab === 'Futures') && (
            <div className="mb-12">
              <div className="mb-5">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <i className="ri-calendar-event-line text-emerald-400"></i>
                  Futures Markets
                </h2>
                <p className="text-slate-500 text-sm mt-0.5">Championship winner, Final Four, and regional winner odds</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {BASE_FUTURES.map(m => <FuturesCard key={m.id} market={m} />)}
              </div>
            </div>
          )}

          {/* PLAYER PROPS SECTION */}
          {(activeTab === 'All' || activeTab === 'Player Props') && (
            <div className="mb-12">
              <div className="mb-5">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <i className="ri-user-star-line text-amber-400"></i>
                  Player Props — Round of 32
                </h2>
                <p className="text-slate-500 text-sm mt-0.5">Points, rebounds, assists and more</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {PLAYER_PROPS.map(p => <PlayerPropCard key={p.id} prop={p} />)}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border border-orange-500/20 p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(43,43,43,0.9) 50%, rgba(20,184,166,0.05) 100%)' }}>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white">Ready to Trade March Madness?</h3>
              <p className="text-slate-400 mb-6 max-w-xl mx-auto mt-2">No house edge. No bet limits. Pure peer-to-peer pricing on every Round of 32 outcome.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/"><button className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-gradient-to-r from-orange-400 to-amber-400 hover:from-orange-500 hover:to-amber-500 text-slate-900 font-bold rounded-lg shadow-lg transition-all cursor-pointer whitespace-nowrap"><i className="ri-home-line"></i>Back to Home</button></a>
                <a href="/super-bowl-props"><button className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-all cursor-pointer whitespace-nowrap"><i className="ri-football-line"></i>Super Bowl Markets</button></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="L2L Logo" className="h-full w-full object-contain" />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 cursor-pointer">Super Bowl</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
